// frontend/src/components/Events/NewsCard.jsx
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Schedule as ScheduleIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const CardContainer = styled(Box)(({ theme }) => ({
  minWidth: 300,
  maxWidth: 300,
  width: 300,
  height: 220,
  position: 'relative',
  cursor: 'pointer',
  flexShrink: 0,
  overflow: 'visible',
  borderRadius: 26,
  transition: 'transform 360ms cubic-bezier(0.22, 1, 0.36, 1), filter 360ms ease',

  '&:hover': {
    transform: 'translateY(-5px)',

    '& .news-media': {
      transform: 'scale(1.035)',
    },

    '& .news-glass-panel': {
      transform: 'translateY(-4px)',
      boxShadow: '0 10px 24px rgba(0,0,0,0.06)',
    },

    '& .news-panel-shine': {
      opacity: 0.72,
      transform: 'translateX(28px)',
    },
  },

  '&:focus-visible': {
    outline: 'none',

    '& .news-glass-panel': {
      boxShadow: '0 10px 24px rgba(0,0,0,0.06)',
    },
  },
}));

/*
 * Нижний слой.
 * Фото занимает ВСЮ площадь карточки и обрезается object-fit: cover.
 */
const PhotoLayer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  borderRadius: 26,
  background: theme.palette.grey[200],
  border: `1px solid ${alpha(theme.palette.common.white, 0.46)}`,
  boxShadow: '0 8px 18px rgba(0, 0, 0, 0.08)',
  isolation: 'isolate',

  '& .news-media': {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 650ms cubic-bezier(0.22, 1, 0.36, 1)',
    filter: 'saturate(0.98) contrast(0.98)',
  },

  /*
   * Тонкая световая кромка. Она не является обычной рамкой:
   * блик сильнее на верхнем левом радиусе и постепенно растворяется
   * по поверхности, создавая ощущение выпуклого стекла.
   */
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0.5px',
    zIndex: 3,
    pointerEvents: 'none',
    borderRadius: 25,
    background: `
      radial-gradient(
        ellipse at 18% 4%,
        rgba(255,255,255,0.62) 0%,
        rgba(255,255,255,0.24) 12%,
        rgba(255,255,255,0.00) 34%
      ),
      linear-gradient(
        145deg,
        rgba(255,255,255,0.56) 0%,
        rgba(255,255,255,0.24) 12%,
        rgba(255,255,255,0.045) 32%,
        rgba(255,255,255,0.00) 58%,
        rgba(255,255,255,0.08) 80%,
        rgba(255,255,255,0.24) 93%,
        rgba(255,255,255,0.44) 100%
      )
    `,
    boxShadow: `
      inset 0 2px 0 rgba(255,255,255,0.82),
      inset 2px 0 0 rgba(255,255,255,0.52),
      inset -2px 0 0 rgba(255,255,255,0.30),
      inset 0 -2px 0 rgba(255,255,255,0.26),
      inset 0 0 14px rgba(255,255,255,0.14)
    `,
  },

  /*
   * Лёгкая кривизна линзы по краям: центр остаётся чистым,
   * а кромка получает мягкое затемнение и ощущение преломления.
   */
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    pointerEvents: 'none',
    borderRadius: 26,

    // Главный эффект: не тень, а реальное размытие изображения у внутренних краёв.
    // Маска оставляет центр резким и постепенно усиливает blur к краям.
    backdropFilter: 'blur(20px) saturate(112%)',
    WebkitBackdropFilter: 'blur(20px) saturate(112%)',
    maskImage: `radial-gradient(ellipse at center,
      transparent 0%,
      transparent 88%,
      rgba(0,0,0,0.18) 93%,
      rgba(0,0,0,0.58) 97%,
      #000 100%
    )`,
    WebkitMaskImage: `radial-gradient(ellipse at center,
      transparent 0%,
      transparent 88%,
      rgba(0,0,0,0.18) 93%,
      rgba(0,0,0,0.58) 97%,
      #000 100%
    )`,

    background: `
      radial-gradient(
        ellipse at 50% 50%,
        rgba(0,0,0,0) 28%,
        rgba(12,22,36,0.025) 42%,
        rgba(12,22,36,0.065) 58%,
        rgba(12,22,36,0.13) 76%,
        rgba(12,22,36,0.20) 100%
      ),
      linear-gradient(
        180deg,
        rgba(255,255,255,0.06) 0%,
        rgba(255,255,255,0.012) 30%,
        rgba(255,255,255,0.00) 50%,
        rgba(17,28,46,0.08) 78%,
        rgba(17,28,46,0.16) 100%
      )
    `,
    boxShadow: `
      inset 0 0 0 3px rgba(255,255,255,0.07),
      inset 0 0 22px rgba(10,20,35,0.12)
    `,
  },
}));

const MediaIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 5,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minHeight: 27,
  padding: '4px 8px',
  borderRadius: 999,
  color: theme.palette.common.white,
  backgroundColor: 'rgba(18, 26, 42, 0.24)',
  border: '1px solid rgba(255,255,255,0.42)',
  backdropFilter: 'blur(14px) saturate(145%)',
  WebkitBackdropFilter: 'blur(14px) saturate(145%)',
  boxShadow: '0 5px 14px rgba(0,0,0,0.08)',
  fontSize: '0.70rem',
  fontWeight: 650,

  '& svg': {
    fontSize: 14,
  },
}));

const SectionChip = styled(Chip)(({ theme, chipcolor }) => ({
  position: 'absolute',
  top: 10,
  left: 10,
  zIndex: 5,
  height: 27,
  maxWidth: '62%',
  color: chipcolor || theme.palette.primary.main,
  backgroundColor: 'rgba(255,255,255,0.32)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.44)}`,
  backdropFilter: 'blur(14px) saturate(145%)',
  WebkitBackdropFilter: 'blur(14px) saturate(145%)',
  boxShadow: '0 5px 14px rgba(0,0,0,0.07)',

  '& .MuiChip-label': {
    px: 1.15,
    fontSize: '0.70rem',
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

/*
 * Верхний слой — компактная белая матовая glass-плашка.
 * Она почти совпадает по ширине с фотографией.
 */
const GlassPanel = styled(Box)(({ theme, accent }) => ({
  position: 'absolute',
  left: 7,
  right: 7,
  bottom: 7,
  zIndex: 6,
  minHeight: 70,
  padding: theme.spacing(1.05, 1.35, 0.95),
  borderRadius: 20,
  overflow: 'hidden',

  background: `
    linear-gradient(
      145deg,
      rgba(255,255,255,0.44) 0%,
      rgba(255,255,255,0.28) 55%,
      rgba(255,255,255,0.34) 100%
    )
  `,
  backgroundColor: 'rgba(255,255,255,0.30)',

  backdropFilter: 'blur(30px) saturate(155%)',
  WebkitBackdropFilter: 'blur(30px) saturate(155%)',

  border: `1px solid ${alpha(theme.palette.common.white, 0.52)}`,

  boxShadow: 'none',

  transition: `
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 360ms ease
  `,

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: `
      radial-gradient(
        circle at 12% 0%,
        rgba(255,255,255,0.30),
        transparent 38%
      ),
      linear-gradient(
        90deg,
        transparent 0%,
        rgba(255,255,255,0.08) 48%,
        transparent 100%
      )
    `,
  },

}));

const PanelShine = styled(Box)(() => ({
  position: 'absolute',
  top: -50,
  left: -90,
  width: 100,
  height: 150,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.24)',
  filter: 'blur(18px)',
  opacity: 0.42,
  transform: 'translateX(0)',
  pointerEvents: 'none',
  transition: 'opacity 360ms ease, transform 620ms cubic-bezier(0.22, 1, 0.36, 1)',
}));

const NewsCard = ({
  news,
  sections = [],
  isLiked = false,
  onLikeToggle,
  onClick
}) => {
  const theme = useTheme();

  const getFirstMedia = (media) => {
    if (!media || media.length === 0) return null;
    return media[0];
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return 'только что';
      if (diffMins < 60) return `${diffMins} мин. назад`;
      if (diffHours < 24) return `${diffHours} ч. назад`;
      if (diffDays === 1) return 'вчера';
      if (diffDays < 7) return `${diffDays} дн. назад`;

      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const firstMedia = getFirstMedia(news.media);
  const section = sections.find((s) => s.id === news.section);
  const sectionColor = section?.color || theme.palette.primary.main;

  const handleLikeClick = (e) => {
    e.stopPropagation();
    onLikeToggle(news.id, e);
  };

  return (
    <CardContainer
      onClick={onClick}
      role="article"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <PhotoLayer>
        {firstMedia ? (
          firstMedia.type === 'video' ? (
            <video
              className="news-media"
              src={firstMedia.url}
              muted={news.video_plays_automuted}
              autoPlay={false}
              loop
              playsInline
            />
          ) : (
            <img
              className="news-media"
              src={firstMedia.url}
              alt={news.title}
            />
          )
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme.palette.grey[100],
            }}
          >
            <ImageIcon sx={{ fontSize: 42, color: alpha(sectionColor, 0.40) }} />
          </Box>
        )}

        {news.media?.length > 1 && (
          <MediaIndicator>
            {news.media[0].type === 'video' ? <VideoIcon /> : <ImageIcon />}
            {news.media.length}
          </MediaIndicator>
        )}

        {section && (
          <SectionChip
            label={section.title}
            size="small"
            chipcolor={section.color}
          />
        )}
      </PhotoLayer>

      <GlassPanel
        className="news-glass-panel"
        accent={sectionColor}
      >
        <PanelShine className="news-panel-shine" />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.30,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.45,
              minWidth: 0,
              color: alpha(theme.palette.text.primary, 0.64),
            }}
          >
            <ScheduleIcon sx={{ fontSize: 14 }} />

            <Typography
              variant="caption"
              sx={{
                fontSize: '0.70rem',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {formatDate(news.created_at)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              color: alpha(theme.palette.text.primary, 0.62),
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <VisibilityIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontSize: '0.70rem' }}>
                {news.views_count || 0}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15 }}>
              <IconButton
                size="small"
                onClick={handleLikeClick}
                aria-label={isLiked ? 'Убрать лайк' : 'Поставить лайк'}
                sx={{
                  width: 22,
                  height: 22,
                  p: 0,
                  color: isLiked
                    ? '#FF375F'
                    : alpha(theme.palette.text.primary, 0.62),
                  transition: 'transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1), color 180ms ease',
                  '&:hover': {
                    transform: 'scale(1.18)',
                    color: '#FF375F',
                    backgroundColor: alpha('#FF375F', 0.07),
                  },
                  '&:active': {
                    transform: 'scale(1.38)',
                  },
                }}
              >
                {isLiked ? (
                  <FavoriteIcon
                    sx={{
                      fontSize: 16,
                      filter: 'drop-shadow(0 0 4px rgba(255,55,95,0.24))',
                      animation: 'heartBeat 0.38s ease-in-out',
                    }}
                  />
                ) : (
                  <FavoriteBorderIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>

              <Typography variant="caption" sx={{ fontSize: '0.70rem' }}>
                {news.likes_count || 0}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{
            position: 'relative',
            zIndex: 2,
            color: '#14284A',
            fontSize: '0.90rem',
            lineHeight: 1.14,
            fontWeight: 720,
            letterSpacing: '-0.012em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pr: news.created_by ? 3.4 : 0,
          }}
        >
          {news.title}
        </Typography>

        {news.created_by && (
          <Tooltip
            title={`Автор: ${news.created_by.full_name || news.created_by.email}`}
          >
            <Avatar
              src={news.created_by.avatar}
              sx={{
                position: 'absolute',
                right: 10,
                bottom: 9,
                width: 23,
                height: 23,
                border: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
                boxShadow: `0 4px 10px ${alpha(theme.palette.common.black, 0.10)}`,
                fontSize: '0.62rem',
                zIndex: 3,
              }}
            >
              {news.created_by.full_name?.charAt(0) || '?'}
            </Avatar>
          </Tooltip>
        )}
      </GlassPanel>

      <style>{`
        @keyframes heartBeat {
          0% { transform: scale(1); }
          25% { transform: scale(1.28); }
          50% { transform: scale(0.96); }
          75% { transform: scale(1.16); }
          100% { transform: scale(1); }
        }
      `}</style>
    </CardContainer>
  );
};

export default NewsCard;
